import { Box, Button } from '@mui/material';
import { useMemo } from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import { useValuationsStore } from '../../../store/valuationsStore';
import { REPORT_UNAVAILABLE_MESSAGE, useAssetReportHtml } from './hooks/useAssetReportHtml';
import { ReportHtmlRenderer } from './ReportHtmlRenderer';
import { ReportMessage } from './ReportMessage';
import { ReportSkeleton } from './ReportSkeleton';
import type { AssetIdentifier } from './types';

const assetReportContainerStyles = {};

const buttonContainerStyles = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '1rem',
};

const loadButtonStyles = {};

const AssetReportContent = () => {
  const selectedAsset = useValuationsStore.use.selectedAssetDetails();

  const asset = useMemo((): AssetIdentifier | null => {
    if (!selectedAsset?.legacyId) {
      return null;
    }

    return {
      legacyId: selectedAsset.legacyId,
      isoCode: selectedAsset.country?.isoCode ?? '',
    };
  }, [selectedAsset]);

  const { html, status, shouldCleanDom, resetCleanDom, loadReport } = useAssetReportHtml({
    asset,
  });

  if (!selectedAsset) {
    return null;
  }

  if (status === 'loading') {
    return <ReportSkeleton />;
  }

  if (status === 'idle') {
    return (
      <Box sx={buttonContainerStyles}>
        <Button
          variant='outlined'
          color='secondary'
          onClick={loadReport}
          sx={loadButtonStyles}
          data-id='load-asset-report-button'
        >
          Load Asset Report
        </Button>
      </Box>
    );
  }

  if (status === 'error' || status === 'unavailable') {
    return <ReportMessage message={html ?? REPORT_UNAVAILABLE_MESSAGE} />;
  }

  if (status === 'success' && html) {
    return (
      <ReportHtmlRenderer
        html={html}
        shouldCleanDom={shouldCleanDom}
        onCleanupComplete={resetCleanDom}
      />
    );
  }

  return <ReportMessage message={REPORT_UNAVAILABLE_MESSAGE} />;
};

const AssetReport = () => {
  return (
    <Box component='section' sx={assetReportContainerStyles} data-id='asset-report-preview'>
      <ErrorBoundary>
        <AssetReportContent />
      </ErrorBoundary>
    </Box>
  );
};

export { AssetReport };
